import { useEffect, useRef, useState } from "react";
import FormWrapper from "../FormWrapper/FormWrapper";
import {
  generateWalkInPassword,
  slugifyUserName,
} from "../../../../utils/adminWalkInBooking";
import {
  searchWalkInUsers,
  suggestWalkInUserName,
} from "../../../../api/adminWalkInBookingApi";

const CREATE_FOR_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Sender", label: "Sender" },
  { value: "Receiver", label: "Receiver" },
  { value: "Both", label: "Both" },
];

const MODE_OPTIONS = [
  { value: "None", label: "None" },
  { value: "New", label: "New" },
  { value: "Existing", label: "Existing" },
];

const partyEnabled = (createFor, party) =>
  createFor === "Both" || createFor === party;

const UserTypeahead = ({ valueLabel, onSelect, disabled }) => {
  const [query, setQuery] = useState(valueLabel || "");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(valueLabel || "");
  }, [valueLabel]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (disabled || query.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchWalkInUsers(query);
        setResults(rows);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query, disabled]);

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        disabled={disabled}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onSelect({ id: "", userName: "", name: "", email: "" });
        }}
        onFocus={() => results.length > 0 && setOpen(true)}
        placeholder="Search name, username, or email"
        className="w-full h-9 px-3 rounded-lg border border-stone-300 bg-stone-50 text-sm outline-none focus:bg-white focus:border-orange-500"
      />
      {open && !disabled && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-stone-200 bg-white shadow-lg">
          {loading ? (
            <li className="px-3 py-2 text-sm text-stone-500">Searching…</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2 text-sm text-stone-500">No users found</li>
          ) : (
            results.map((user) => (
              <li key={user.id || user.userName}>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-stone-50"
                  onClick={() => {
                    onSelect(user);
                    setQuery(
                      [user.userName, user.name, user.email]
                        .filter(Boolean)
                        .join(" · "),
                    );
                    setOpen(false);
                  }}
                >
                  <span className="block text-sm font-semibold text-stone-900">
                    {user.userName || user.name || "User"}
                  </span>
                  <span className="block text-xs text-stone-500">
                    {[user.name, user.email, user.mobile]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const PartyCard = ({
  party,
  enabled,
  data,
  name,
  email,
  updateFields,
}) => {
  const prefix = party === "Sender" ? "walkInSender" : "walkInReceiver";
  const mode = enabled ? data[`${prefix}Mode`] || "None" : "None";

  useEffect(() => {
    if (!enabled && mode !== "None") {
      updateFields({
        [`${prefix}Mode`]: "None",
        [`${prefix}ExistingUserId`]: "",
        [`${prefix}ExistingLabel`]: "",
        [`${prefix}UserName`]: "",
        [`${prefix}Email`]: "",
        [`${prefix}Password`]: "",
      });
    }
  }, [enabled, mode, prefix, updateFields]);

  useEffect(() => {
    if (!enabled || mode !== "New") return undefined;
    const handle = setTimeout(async () => {
      if (!name) return;
      try {
        const suggested = await suggestWalkInUserName(name);
        updateFields({
          [`${prefix}UserName`]: suggested || slugifyUserName(name),
        });
      } catch {
        updateFields({ [`${prefix}UserName`]: slugifyUserName(name) });
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [enabled, mode, name, prefix, updateFields]);

  useEffect(() => {
    if (enabled && mode === "New" && email && !data[`${prefix}Email`]) {
      updateFields({ [`${prefix}Email`]: email });
    }
  }, [enabled, mode, email, data, prefix, updateFields]);

  return (
    <div
      className={`rounded-xl border p-3 ${
        enabled ? "border-stone-200 bg-white" : "border-stone-100 bg-stone-50 opacity-70"
      }`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-sm font-bold text-stone-900">{party}</h3>
        <div className="inline-flex rounded-lg border border-stone-200 p-0.5 bg-stone-50">
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={!enabled}
              onClick={() =>
                updateFields({
                  [`${prefix}Mode`]: opt.value,
                  ...(opt.value !== "Existing"
                    ? {
                        [`${prefix}ExistingUserId`]: "",
                        [`${prefix}ExistingLabel`]: "",
                      }
                    : {}),
                  ...(opt.value !== "New"
                    ? {
                        [`${prefix}UserName`]: "",
                        [`${prefix}Password`]: "",
                      }
                    : {
                        [`${prefix}Password`]:
                          data[`${prefix}Password`] || generateWalkInPassword(),
                      }),
                })
              }
              className={`px-3 h-8 rounded-md text-xs font-semibold ${
                mode === opt.value
                  ? "bg-stone-900 text-white"
                  : "text-stone-600"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {!enabled ? (
        <p className="text-xs text-stone-500">
          Not included in account creation.
        </p>
      ) : mode === "Existing" ? (
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
            Existing user
          </span>
          <div className="mt-1.5">
            <UserTypeahead
              valueLabel={data[`${prefix}ExistingLabel`]}
              onSelect={(user) =>
                updateFields({
                  [`${prefix}ExistingUserId`]: user.id,
                  [`${prefix}ExistingLabel`]: [user.userName, user.name, user.email]
                    .filter(Boolean)
                    .join(" · "),
                })
              }
            />
          </div>
        </label>
      ) : mode === "New" ? (
        <div className="grid gap-3">
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
              Username
            </span>
            <input
              readOnly
              value={data[`${prefix}UserName`] || ""}
              className="mt-1.5 w-full h-9 px-3 rounded-lg border border-stone-200 bg-stone-100 text-sm text-stone-700"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
              Email (optional)
            </span>
            <input
              type="email"
              value={data[`${prefix}Email`] || ""}
              onChange={(e) =>
                updateFields({ [`${prefix}Email`]: e.target.value })
              }
              placeholder="Optional"
              className="mt-1.5 w-full h-9 px-3 rounded-lg border border-stone-300 bg-stone-50 text-sm outline-none focus:bg-white focus:border-orange-500"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
              Password
            </span>
            <div className="mt-1.5 flex gap-2">
              <input
                type="text"
                required
                value={data[`${prefix}Password`] || ""}
                onChange={(e) =>
                  updateFields({ [`${prefix}Password`]: e.target.value })
                }
                placeholder="Required"
                className="flex-1 h-9 px-3 rounded-lg border border-stone-300 bg-stone-50 text-sm outline-none focus:bg-white focus:border-orange-500"
              />
              <button
                type="button"
                onClick={() =>
                  updateFields({
                    [`${prefix}Password`]: generateWalkInPassword(),
                  })
                }
                className="h-9 px-3 rounded-lg border border-stone-200 bg-white text-xs font-semibold text-stone-700 hover:bg-stone-50"
              >
                Generate
              </button>
            </div>
          </label>
        </div>
      ) : (
        <p className="text-xs text-stone-500">No login will be created.</p>
      )}
    </div>
  );
};

const WalkInAccountPanel = ({
  walkInCreateAccountsFor,
  walkInBookingOwner,
  senderName,
  senderEmail,
  receiverName,
  receiverEmail,
  updateFields,
  ...data
}) => {
  const createFor = walkInCreateAccountsFor || "None";
  const owner = walkInBookingOwner || "Sender";

  return (
    <FormWrapper title="Accounts">
      <div className="w-full space-y-3">
        <div>
          <h2 className="text-sm font-semibold text-stone-900">
            Create accounts
          </h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Optional logins for the sender and/or receiver. Existing users are
            found by typeahead. New usernames come from the server.
          </p>
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-2">
            Create accounts for
          </p>
          <div className="inline-flex flex-wrap rounded-xl border border-stone-200 p-1 bg-stone-50 gap-1">
            {CREATE_FOR_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() =>
                  updateFields({ walkInCreateAccountsFor: opt.value })
                }
                className={`px-2.5 h-7 rounded-md text-xs font-semibold ${
                  createFor === opt.value
                    ? "bg-orange-600 text-white"
                    : "text-stone-700 hover:bg-white"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <PartyCard
            party="Sender"
            enabled={partyEnabled(createFor, "Sender")}
            data={{ walkInCreateAccountsFor: createFor, ...data }}
            name={senderName}
            email={senderEmail}
            updateFields={updateFields}
          />
          <PartyCard
            party="Receiver"
            enabled={partyEnabled(createFor, "Receiver")}
            data={{ walkInCreateAccountsFor: createFor, ...data }}
            name={receiverName}
            email={receiverEmail}
            updateFields={updateFields}
          />
        </div>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-2">
            Booking owner
          </p>
          <div className="flex gap-4">
            {["Sender", "Receiver"].map((opt) => (
              <label
                key={opt}
                className="inline-flex items-center gap-2 text-sm font-semibold text-stone-800"
              >
                <input
                  type="radio"
                  name="walkInBookingOwner"
                  value={opt}
                  checked={owner === opt}
                  onChange={() => updateFields({ walkInBookingOwner: opt })}
                  className="accent-orange-600"
                />
                {opt}
              </label>
            ))}
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};

export default WalkInAccountPanel;
